import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { IActionsData, IAppTableBase } from './AppTable'
import { ColPagination } from './tableMobileComponents/ColPagination'
import { TableColgroup } from './tableMobileComponents/TableColgroup'
import { THeadMobileDndBlock } from './tableMobileDndComponents/THeadMobileDndBlock'
import { TableMobileRow } from './tableMobileComponents/TableMobileRow'
import { useSearchParams } from 'react-router-dom'
import { AppScrollWrapperNew } from '../../widgets/AppScrollWrapper/AppScrollWrapper'

import { TableActionsBlock } from './tableComponents/TableActionsBlock'
import { TablePaginationBlock } from './tableComponents/TablePaginationBlock'
import { useTranslation } from 'react-i18next'
import { useGetWidth } from 'utils/useGetWidth'
import LoaderRow from 'components/loaderRow/LoaderRow'
import { IChooseItems } from 'core/api/dto/BaseDto'

export interface IHeaderData {
  title: string
  sort?: string
  colWidth?: string // px or %
  order?: number
  startPosition?: number
  subData?: string[]
}
interface IAppTableDndMobileProps<T, TKey> extends IAppTableBase<T, TKey> {
  params?: URLSearchParams
  isDefaultOpen?: boolean
  rowClassName?: string
  tableCellClassName?: string
  tableClassName?: string
  tableWrapperClassName?: string
  toggleAllState?: number[]
  setToggleAllState?: Dispatch<SetStateAction<number[]>>
  chooseActions?: IActionsData[]
  chooseMode?: boolean
  chooseItems?: IChooseItems | null
  setChooseItems?: React.Dispatch<React.SetStateAction<IChooseItems | null>>
  setHeaderData: (value: IHeaderData[]) => void
  tableName: string
}
interface ISelectorBase<TKey> {
  propsNameToDisable?: TKey
}
interface ISelectorName<TKey> extends ISelectorBase<TKey> {
  name: TKey | null
  to?: TKey
  toDisabledTo?: boolean
  isDelete?: TKey
  renderItem?: never
}
export type TSelector<T, TKey> = ISelectorName<TKey> | ISelectorVoid<T>
interface ISelectorVoid<T> extends ISelectorBase<keyof T> {
  name?: never
  to?: never
  toDisabledTo?: never
  isDelete?: never
  renderItem: (item: T, index: number) => JSX.Element | string
}
export const AppTableDndMobile = <
  T extends { id: number; archived?: boolean; isSynced?: boolean },
  TKey extends keyof T,
>({
  params,
  isDefaultOpen,
  headerData,
  data,
  tableDataSelectors,
  subTableRepresent,
  setCurrentSortFn,
  rowClassName,
  tableCellClassName,
  tableClassName,
  tableWrapperClassName,
  toggleAllState,
  setToggleAllState,
  chooseMode,
  chooseActions,
  chooseItems,
  setChooseItems,
  isLoading,
  setTableData: setTableDataOut,
  setHeaderData,
  tableName,
}: IAppTableDndMobileProps<T, TKey>) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams(params)
  const offset = Number(searchParams.get('offset')) || 0
  const [loadMore, setLoadMore] = useState(false)
  const { width } = useGetWidth()
  const mobileMode = useMemo(() => width < 600, [width])
  const tabletMode = useMemo(() => width <= 1200, [width])
  const [tableData, setTableData] = useState<T[]>([])
  const [currentCol, setCurrentCol] = useState(1)
  const tableRef = useRef<null | HTMLDivElement>(null)
  const [tableSelectors, setTableSelectors] = useState<TSelector<T, TKey>[]>([])
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [isLazyLoadingSubtables, setIsLazyLoadingSubtables] = useState(false)
  const isToggleAllActive = toggleAllState?.length === tableData.length
  useEffect(() => {
    setCurrentCol(1)
  }, [width])

  useEffect(() => {
    if (data.results && !offset && !loadMore && !data.previous) {
      setTableData(data.results)
      setTableDataOut?.(data.results)
    }
    if (data.results && !!offset && loadMore) {
      setTableData((prev) => [...prev, ...data.results])
      setTableDataOut?.([...tableData, ...data.results])
      setLoadMore(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  useEffect(() => {
    setTableSelectors(
      tableDataSelectors.map((_, index) => {
        return index === 0
          ? tableDataSelectors[0]
          : tableDataSelectors[`${headerData[index]?.startPosition || 0}`]
      }),
    )
  }, [headerData, tableDataSelectors])
  const chooseItemsHandler = useCallback((value: boolean, id: number) => {
    setChooseItems &&
      setChooseItems((prevState) => ({ ...prevState, [id]: value }))
  }, [])

  const SUBTABLE_SAFE_STEP = 100
  const [expandedDimensionsCount, setExpandedDimensionsCount] = useState(0)
  const expandedIndices = toggleAllState || []

  const isAllOpen = expandedIndices.length === tableData.length
  const onToggleAll = () => {
    if (isAllOpen) {
      setToggleAllState?.([])
      setExpandedDimensionsCount(0)
    } else {
      setToggleAllState?.(tableData.map((_, i) => i))

      const firstFewDims = tableData
        .slice(1)
        .map((el: any) => el.dimensionsCount ?? 0)

      const initialCount = firstFewDims.length
        ? firstFewDims[0] + SUBTABLE_SAFE_STEP
        : SUBTABLE_SAFE_STEP

      setExpandedDimensionsCount(initialCount)
    }
  }

  const [showLazyLoader, setShowLazyLoader] = useState(false)
  const lastExpandedRowRef = useRef<HTMLTableRowElement | null>(null)

  useEffect(() => {
    if (!isToggleAllActive || isLazyLoadingSubtables) return

    const scrollEl = scrollRef.current
    const targetEl = lastExpandedRowRef.current
    if (!scrollEl || !targetEl) return

    let observer: IntersectionObserver | null = null

    const triggerLazyLoad = () => {
      if (isLazyLoadingSubtables) return
      setShowLazyLoader(true)
      setIsLazyLoadingSubtables(true)
      const CHUNK_SIZE = 30
      const totalToAdd = SUBTABLE_SAFE_STEP
      let added = 0

      const chunkLoad = () => {
        setExpandedDimensionsCount((prev) => prev + CHUNK_SIZE)
        added += CHUNK_SIZE

        if (added < totalToAdd) {
          setTimeout(chunkLoad, 16)
        } else {
          setTimeout(() => {
            const scrollEl = scrollRef.current
            if (!scrollEl) return setIsLazyLoadingSubtables(false)

            const isAtOrNearBottom =
              scrollEl.scrollHeight -
                scrollEl.scrollTop -
                scrollEl.clientHeight <
              100

            const hasMoreToLoad =
              expandedDimensionsCount <
              tableData.reduce(
                (acc, el) => acc + ((el as any).dimensionsCount || 0),
                0,
              )

            if (isAtOrNearBottom && hasMoreToLoad) {
              triggerLazyLoad()
            } else {
              setTimeout(() => {
                setIsLazyLoadingSubtables(false)
                setShowLazyLoader(false)
              }, 400) // задержка
            }
          }, 100)
        }
      }

      chunkLoad()
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          triggerLazyLoad()
        }
      },
      {
        root: scrollEl,
        threshold: 0.01,
        rootMargin: '1000px 0px 1000px 0px',
      },
    )

    observer.observe(targetEl)

    const onScroll = () => {
      const atBottom =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 300

      const hasMore =
        expandedDimensionsCount <
        tableData.reduce(
          (acc, el) => acc + ((el as any).dimensionsCount || 0),
          0,
        )

      if (atBottom && hasMore) {
        triggerLazyLoad()
      }
    }

    scrollEl.addEventListener('scroll', onScroll)

    return () => {
      observer?.disconnect()
      scrollEl.removeEventListener('scroll', onScroll)
    }
  }, [
    expandedDimensionsCount,
    isToggleAllActive,
    isLazyLoadingSubtables,
    tableData,
  ])

  const getExpandedDimensionsCountByIndex = (index: number) => {
    let sum = 0
    for (let i = 1; i <= index && i < tableData.length; i++) {
      sum += (tableData[i] as any).dimensionsCount ?? 0
    }
    return sum
  }
  const lastExpandedIndex = useMemo(() => {
    for (let i = 0; i < tableData.length; i++) {
      const dimsCount = getExpandedDimensionsCountByIndex(i)
      const nextDimsCount = getExpandedDimensionsCountByIndex(i + 1) ?? Infinity

      if (
        dimsCount <= expandedDimensionsCount &&
        nextDimsCount > expandedDimensionsCount
      ) {
        return i
      }
    }
    return -1
  }, [tableData, expandedDimensionsCount])

  return (
    <div className="app-table-mobile-wrap">
      <TableActionsBlock
        chooseActions={chooseActions}
        tableData={tableData}
        chooseItems={chooseItems}
        setChooseItems={setChooseItems}
        chooseMode={chooseMode}
      />
      {(mobileMode || tabletMode) && (
        <ColPagination
          setCurrentCol={setCurrentCol}
          totalCol={tableDataSelectors.length - 1}
          currentCol={currentCol}
        />
      )}
      <div
        className={`app-table-mobile ${tableWrapperClassName || ''}`}
        ref={tableRef}
      >
        <AppScrollWrapperNew
          ref={scrollRef}
          theme={'table'}
          trigger={toggleAllState?.join(',')}
        >
          <table className={tableClassName}>
            <TableColgroup
              mobileMode={mobileMode}
              tabletMode={tabletMode}
              headerData={headerData}
              chooseMode={chooseMode}
            />
            <THeadMobileDndBlock
              isAllOpen={isAllOpen}
              onToggleAll={
                toggleAllState && setToggleAllState ? onToggleAll : undefined
              }
              setHeaderData={setHeaderData}
              headerData={headerData}
              mobileMode={mobileMode || tabletMode}
              currentCol={currentCol}
              setCurrentCol={setCurrentCol}
              setCurrentSortFn={setCurrentSortFn}
              chooseMode={chooseMode}
              tableName={tableName}
            />
            <tbody>
              {!tableData.length && (
                <tr>
                  <td
                    colSpan={tableDataSelectors.length}
                    className="result-not-found"
                  >
                    <div>{t('Выберите фильтры')}</div>
                  </td>
                </tr>
              )}

              {tableData.map((item, itemIndex) => {
                const isExpanded = toggleAllState?.includes(itemIndex)
                const dimsCountAtIndex =
                  getExpandedDimensionsCountByIndex(itemIndex)
                // const isLastExpanded =
                //   dimsCountAtIndex <= expandedDimensionsCount &&
                //   (getExpandedDimensionsCountByIndex(itemIndex + 1) ??
                //     Infinity) > expandedDimensionsCount

                const shouldRenderSubTable =
                  isExpanded &&
                  (isToggleAllActive
                    ? dimsCountAtIndex <= expandedDimensionsCount
                    : true) // одиночное открытие — показывай всегда
                return (
                  <TableMobileRow
                    setToggleAllState={setToggleAllState}
                    toggleAllState={toggleAllState}
                    tableCellClassName={tableCellClassName}
                    rowClassName={rowClassName}
                    isDefaultOpen={isDefaultOpen}
                    tableDataSelectors={tableSelectors}
                    item={item}
                    headerData={headerData}
                    itemIndex={itemIndex}
                    key={`table-row-c-${item?.id}-${itemIndex}`}
                    shouldRenderSubTable={shouldRenderSubTable}
                    subTableRepresent={subTableRepresent}
                    mobileMode={mobileMode || tabletMode}
                    currentCol={currentCol}
                    setCurrentCol={setCurrentCol}
                    chooseItemsHandler={chooseItemsHandler}
                    chooseItems={chooseItems}
                    chooseMode={chooseMode}
                    rowRef={
                      itemIndex === lastExpandedIndex
                        ? lastExpandedRowRef
                        : undefined
                    }
                  />
                )
              })}
            </tbody>
          </table>
        </AppScrollWrapperNew>

        {isToggleAllActive && showLazyLoader && <LoaderRow />}
        <TablePaginationBlock
          isLoading={!!isLoading}
          dataCount={data.count}
          dataNext={!!data.next}
          tableRef={tableRef}
        />
      </div>
    </div>
  )
}
